import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { cn } from '../lib/utils';

const SuggestedCompetitorsNew = ({ productId, onCompetitorsAdded }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [adding, setAdding] = useState(false);
  const { getToken } = useAuth();

  useEffect(() => {
    fetchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/competitors/products/${productId}/suggested-competitors`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestions(response.data);
    } catch (error) {
      console.error('خطا در دریافت پیشنهادات:', error);
      toast.error('خطا در دریافت رقبای پیشنهادی');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === suggestions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(suggestions.map(s => s.product_id));
    }
  };

  const handleAddSelected = async () => {
    if (selectedIds.length === 0) {
      toast.warning('لطفاً حداقل یک رقیب انتخاب کنید');
      return;
    }

    try {
      setAdding(true);
      const token = getToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/competitors/products/${productId}/add-suggested-competitors`,
        {
          product_id: productId,
          competitor_ids: selectedIds
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${response.data.added_count} رقیب با موفقیت اضافه شد`);
      await fetchSuggestions();
      setSelectedIds([]);

      if (onCompetitorsAdded) {
        onCompetitorsAdded();
      }
    } catch (error) {
      console.error('خطا در افزودن رقبا:', error);
      toast.error('خطا در افزودن رقبا');
    } finally {
      setAdding(false);
    }
  };

  const formatPrice = (price) => {
    return Math.round(price / 10).toLocaleString('fa-IR').replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
  };

  const getSimilarityVariant = (score) => {
    if (score >= 80) return { variant: "default", bg: "bg-green-500" };
    if (score >= 60) return { variant: "default", bg: "bg-blue-500" };
    if (score >= 40) return { variant: "secondary", bg: "bg-yellow-500" };
    return { variant: "outline", bg: "bg-gray-400" };
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <span className="text-muted-foreground">در حال جستجوی رقبا...</span>
          </div>
        </div>
      </Card>
    );
  }

  if (suggestions.length === 0) {
    return (
      <Card className="p-12 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="rounded-full bg-muted p-6">
            <SparklesIcon className="h-12 w-12 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">رقیب پیشنهادی یافت نشد</h3>
            <p className="text-muted-foreground mt-2">
              محصولات مشابهی در باسلام وجود ندارد
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden shadow-lg">
      {/* Header */}
      <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <SparklesIcon className="h-6 w-6 text-purple-600" />
              <h3 className="text-xl font-bold">رقبای پیشنهادی</h3>
              <Badge variant="secondary">{suggestions.length}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              محصولات مشابه با امتیازدهی هوشمند
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleSelectAll}
              size="sm"
            >
              {selectedIds.length === suggestions.length ? 'لغو همه' : 'انتخاب همه'}
            </Button>

            <Button
              onClick={handleAddSelected}
              disabled={selectedIds.length === 0 || adding}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700"
            >
              {adding ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                  افزودن...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 ml-2" />
                  افزودن ({selectedIds.length})
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* لیست رقبا */}
      <div className="max-h-[600px] overflow-y-auto">
        {suggestions.map((competitor, index) => {
          const isSelected = selectedIds.includes(competitor.product_id);
          const { bg } = getSimilarityVariant(competitor.similarity_score);

          return (
            <div
              key={competitor.product_id}
              className={cn(
                "p-4 border-b transition-colors cursor-pointer",
                isSelected ? "bg-purple-50 hover:bg-purple-100" : "hover:bg-muted/50"
              )}
              onClick={() => handleToggle(competitor.product_id)}
            >
              <div className="flex items-center gap-4">
                {/* Custom Checkbox */}
                <div className="flex-shrink-0">
                  <div
                    className={cn(
                      "w-6 h-6 rounded border-2 flex items-center justify-center transition-all",
                      isSelected
                        ? "bg-purple-600 border-purple-600 scale-110"
                        : "border-input hover:border-purple-400"
                    )}
                  >
                    {isSelected && (
                      <CheckIcon className="h-4 w-4 text-white" />
                    )}
                  </div>
                </div>

                {/* تصویر */}
                <div className="flex-shrink-0">
                  <img
                    src={competitor.photo_url || '/placeholder.png'}
                    alt={competitor.title}
                    className="w-16 h-16 object-cover rounded-lg shadow-sm"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/placeholder.png';
                    }}
                  />
                </div>

                {/* اطلاعات */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold line-clamp-2 mb-2">
                    {competitor.title}
                  </h4>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="font-medium">💰 {formatPrice(competitor.price)}</span>
                    <span className="hidden md:inline">⚖️ {competitor.weight}g</span>
                    {competitor.rating > 0 && (
                      <span className="hidden md:inline">⭐ {competitor.rating.toFixed(1)}</span>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground mt-1 truncate hidden md:block">
                    🏪 {competitor.vendor_name}
                  </p>
                </div>

                {/* امتیاز شباهت */}
                <div className="flex-shrink-0">
                  <Badge
                    className={cn("text-white", bg)}
                  >
                    {competitor.similarity_score}%
                  </Badge>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {selectedIds.length > 0 && (
        <div className="p-4 bg-purple-50 border-t">
          <p className="text-sm text-purple-800 text-center font-medium">
            <CheckIcon className="h-4 w-4 inline ml-1" />
            {selectedIds.length} رقیب انتخاب شده
          </p>
        </div>
      )}
    </Card>
  );
};

export default SuggestedCompetitorsNew;
